/**
 * Edit Billing Rate Modal Component
 * Modal for editing a matter's billing rate
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Input, Button, Stack, Flex, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import type { EchoMatter } from '../../api/types/echo';

export interface EditBillingRateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (matterId: number, newRate: number) => Promise<void>;
  matter: EchoMatter | null;
}

/**
 * Modal for editing billing rate of an Echo matter
 */
export function EditBillingRateModal({
  isOpen,
  onClose,
  onSave,
  matter,
}: EditBillingRateModalProps) {
  const { theme } = useTheme();
  const [billingRate, setBillingRate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const errorColor = theme.colors.semantic.error.default;

  // Reset form when matter changes
  useEffect(() => {
    if (matter) {
      setBillingRate((matter.billing_rate ?? 0).toString());
      setError(null);
    }
  }, [matter]);

  const handleSave = useCallback(async () => {
    if (!matter) return;

    const rate = parseFloat(billingRate);
    if (isNaN(rate) || rate < 0) {
      setError('Please enter a valid positive number');
      return;
    }

    setError(null);
    setIsSaving(true);

    try {
      await onSave(matter.id, rate);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update billing rate');
    } finally {
      setIsSaving(false);
    }
  }, [matter, billingRate, onSave, onClose]);

  const handleClose = useCallback(() => {
    if (!isSaving) {
      setError(null);
      onClose();
    }
  }, [isSaving, onClose]);

  if (!matter) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <Modal.Header onClose={handleClose}>Edit Billing Rate</Modal.Header>
      <Modal.Body>
        <Stack gap="md">
          {/* Matter Info */}
          <div>
            <div style={{ fontSize: fontSize.sm, color: mutedColor }}>Matter</div>
            <div style={{ fontWeight: 500, color: textColor }}>{matter.mattercode}</div>
            <div style={{ fontSize: fontSize.sm, color: mutedColor }}>{matter.name}</div>
          </div>

          {/* Billing Rate Input */}
          <Stack gap="xs">
            <label style={{ ...textStyles.label, color: textColor }}>
              Billing Rate ($/min)
            </label>
            <Flex align="center" gap="sm">
              <span style={{ color: mutedColor }}>$</span>
              <Input
                type="number"
                value={billingRate}
                onChange={(e) => setBillingRate(e.target.value)}
                placeholder="0.00"
                min="0"
                step="0.01"
                disabled={isSaving}
                style={{ flex: 1 }}
              />
              <span style={{ color: mutedColor }}>/min</span>
            </Flex>
            {error && (
              <span style={{ fontSize: fontSize.xs, color: errorColor }}>{error}</span>
            )}
          </Stack>
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Flex justify="end" gap="sm">
          <Button
            variant="outline"
            color="primary"
            onClick={handleClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="solid"
            color="primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  );
}

export default EditBillingRateModal;
